// ActivityEdit.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { Modal, Button, Figure, Alert, Card } from 'react-bootstrap';
import { fetchActivityById, editMultipartForm, fetchOptions, deleteActivity } from '../../api/api'
import AuthenticatedImage from '../../components/AuthenticatedImage';
import AuthenticatedPDF from '../../components/AuthenticatedPDF';

const ActivityEdit = () => {
  const { id } = useParams(); // Get the activity ID from the URL
  const navigate = useNavigate();
  const [activityData, setActivityData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [formValues, setFormValues] = useState({
    materia: '',
    ano: '',
    categoria: '',
    dificuldade: '',
    descricao: '',
    url: '',
    titulo: '',
  });
  const [isFormValidated, setIsFormValidated] = useState(false);
  const [showCard, setShowCard] = useState(true); // State to toggle card visibility
  const fileInputRef = useRef(new Object());
  const formRef = useRef(new Object());
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);




  useEffect(() => {
    const fetchActivityData = async () => {
      try {
        const data = await fetchActivityById(id);
        setFormValues({
          materia: data.subject_uuid,
          ano: data.grade_uuid,
          categoria: data.category_uuid,
          dificuldade: data.difficulty,
          descricao: data.description,
          titulo: data.title,
          url: data.url,
        });

        setActivityData(data);

        setFilteredCategories(prev => ([
          { uuid: data.category_uuid, name: data.category_name },
          ...prev,
        ]));

      } catch (error) {
        // console.error('Error fetching activity data:', error);
      }
    };

    fetchActivityData();
  }, [id]); // The effect depends on the activity id

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
    }
  };

  const handleShowModal = (exercise) => {
    setSelectedExercise(exercise);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedExercise(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormValues({
      ...formValues,
      [name]: value,
    });

  };

  // Fetch subjects on component mount
  useEffect(() => {
    const fetchInitialSubjects = async () => {
      try {
        const options = await fetchOptions(null, null);
        setSubjects(options.subjects || []);
      } catch (error) {
        // console.error("Error fetching subjects:", error);
      }
    };
    fetchInitialSubjects();
  }, []);

  // // Fetch grades when subject changes
  useEffect(() => {
    const fetchGradesForSubject = async () => {
      if (!formValues.materia) return;
      try {
        const options = await fetchOptions(formValues.materia, null);
        setGrades(options.grades || []);
        setFilteredCategories(options.categories || []);

        if (options.grades.filter(grade => grade.uuid == formValues.ano).length == 0) {
          setFormValues({ ...formValues, ano: '' }); // Reset grade and category
        }

      } catch (error) {
        // console.error("Error fetching grades:", error);
      }
    };
    fetchGradesForSubject();
  }, [formValues.materia]);

  // // Fetch categories when grade changes
  useEffect(() => {
    const fetchCategoriesForGrade = async () => {
      if (!formValues.ano) return;
      try {
        const options = await fetchOptions(formValues.materia, formValues.ano);

        setFilteredCategories(options.categories || []);

        if (options.categories.filter(category => category.uuid == formValues.categoria).length == 0) {
          setFormValues({ ...formValues, categoria: '' }); // Reset grade and category
        }
      } catch (error) {
        // console.error("Error fetching categories:", error);
      }
    };
    fetchCategoriesForGrade();
  }, [formValues.ano]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = formRef.current;
    setIsFormValidated(true);

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setShowErrorAlert(true);
    } else {
      // Create a new FormData object to handle multipart form data
      const formData = new FormData();

      // Append form values to formData
      formData.append('subject_uuid', formValues.materia);
      formData.append('year', formValues.ano);
      formData.append('category_uuid', formValues.categoria);
      formData.append('difficulty', formValues.dificuldade);
      formData.append('description', formValues.descricao);
      formData.append('title', formValues.titulo);

      formData.append('url', formValues.url);

      // Append file input to formData (if a file is selected)
      if (fileInputRef.current && fileInputRef.current.files && fileInputRef.current.files[0]) {
        formData.append('file', fileInputRef.current.files[0]); // Assuming file input name is 'file'
      }

      try {
        // Assuming `activityId` is available in the component
        await editMultipartForm(formData, id);

        // After successful submission, show the success alert
        setShowSuccessAlert(true);

        // Remove validation styles
        form.classList.remove('was-validated');
        setIsFormValidated(false);

      } catch (error) {
        console.error('Error submitting form:', error);
        setShowErrorAlert(true);
      }

      setTimeout(() => {
        setShowSuccessAlert(false);
        setShowErrorAlert(false);
      }, 3000);
    }
  };

  const handleRemoveCard = () => {
    setShowCard(false); // Hide the card
  };

  const handleDelete = async () => {
    if (window.confirm("Tem certeza que deseja excluir esta atividade?")) {
      try {
        await deleteActivity(id);
        navigate(`/dashboard/category/${activityData.category_uuid}`);
      } catch (error) {
        console.error("Error deleting activity:", error);
        setShowErrorAlert(true);
      }
    }
  };

  return (
    <div>
      <DashboardLayout title="Editar Atividade">

        {activityData !== null ? (

          <div>
            {/* Success Alert */}
            {showSuccessAlert && (
              <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible>
                Atividade atualizada com sucesso!
              </Alert>
            )}

            {/* Error Alert */}
            {showErrorAlert && (
              <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible>
                Erro ao atualizar atividade. Verifique os campos obrigatórios.
              </Alert>
            )}

            <form
              ref={formRef}
              className={`g-3 needs-validation ${isFormValidated ? 'was-validated' : ''}`}
              noValidate
              onSubmit={handleSubmit}
            >
              <div className="row">
                {/* Left Column: Image/PDF Preview */}
                <div className="col-md-6 mb-3">
                  {/* Conditional rendering based on showCard state */}
                  {showCard ? (
                    <div className="input-group mb-3">
                      <Card className="shadow-sm" style={{ width: '100%', position: 'relative' }}>
                        <div
                          className="position-absolute top-0 end-0 p-2"
                          style={{ zIndex: 10, cursor: 'pointer' }}
                          onClick={handleRemoveCard}
                        >
                          <span className="badge bg-danger">Remover imagem</span>
                        </div>

                        {formValues.url && formValues.url.toLowerCase().endsWith('.pdf') ? (
                          <div className="d-flex justify-content-center align-items-center bg-light" style={{ height: '180px' }}>
                            <i className="bi bi-file-earmark-pdf" style={{ fontSize: '3rem', color: 'red' }}></i>
                          </div>
                        ) : (
                          <AuthenticatedImage variant="top" url={formValues.url} />
                        )}
                        <Card.Body onClick={() => handleShowModal(formValues)} style={{ cursor: 'pointer' }}>
                          <small className="text-muted text-center d-block">Clique para visualizar</small>
                        </Card.Body>
                      </Card>
                    </div>
                  ) : (
                    <div className="input-group mb-3">

                      <label className="input-group-text" htmlFor="inputGroupFile01">Upload</label>
                      <input
                        type="file"
                        className="form-control"
                        id="inputGroupFile01"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        required
                      />
                      <div className="invalid-feedback">
                        Escolha um arquivo.
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column: Form Inputs */}
                <div className="col-md-6">
                  <div className="input-group mb-3">
                    <span className="input-group-text">Título</span>
                    <input
                      className="form-control"
                      aria-label="With textarea"
                      name="titulo"
                      value={formValues.titulo}
                      onChange={handleChange}
                      required
                    ></input>
                    <div className="invalid-feedback">
                      Forneça uma titulo.
                    </div>
                  </div>

                  {/* Form fields */}
                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="inputGroupSelect02">Matéria</label>
                    <select
                      className="form-select"
                      id="inputGroupSelect02"
                      name="materia"
                      value={formValues.materia}
                      onChange={handleChange}
                      required
                    >
                      <option value="" disabled>Escolha...</option>
                      {subjects.map(subject => (
                        <option key={subject.uuid} value={subject.uuid}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                    <div className="invalid-feedback">
                      Escolha uma matéria.
                    </div>
                  </div>

                  {/* Ano (Grade) Select */}
                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="inputGroupSelect03">Ano</label>
                    <select
                      className="form-select"
                      id="inputGroupSelect03"
                      name="ano"
                      value={formValues.ano}
                      onChange={handleChange}
                      required
                      disabled={!grades.length}
                    >
                      <option value="">Escolha...</option>
                      {grades.map((grade) => (
                        <option key={grade.uuid} value={grade.uuid}>
                          {grade.name}
                        </option>
                      ))}
                    </select>
                    <div className="invalid-feedback">
                      Escolha um ano.
                    </div>
                  </div>


                  {/* Categoria (Category) Select */}
                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="inputGroupSelect04">Categoria</label>
                    <select
                      className="form-select"
                      id="inputGroupSelect04"
                      name="categoria"
                      value={formValues.categoria} // Set value to formValues.categoria
                      onChange={handleChange}
                      required
                    >
                      <option value="">Escolha...</option>
                      {filteredCategories.map(category => (
                        <option key={category.uuid} value={category.uuid}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <div className="invalid-feedback">
                      Escolha uma categoria.
                    </div>
                  </div>


                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="inputGroupSelect05">Nível de dificuldade</label>
                    <select
                      className="form-select"
                      id="inputGroupSelect05"
                      name="dificuldade"
                      value={formValues.dificuldade}
                      onChange={handleChange}
                      required
                    >
                      <option value="" disabled>Escolha...</option>
                      <option value="1">Fácil</option>
                      <option value="2">Médio</option>
                      <option value="3">Difícil</option>
                    </select>
                    <div className="invalid-feedback">
                      Escolha um nível.
                    </div>
                  </div>
                  <div className="input-group mb-3">
                    <span className="input-group-text">Descrição</span>
                    <textarea
                      className="form-control"
                      aria-label="With textarea"
                      name="descricao"
                      value={formValues.descricao}
                      onChange={handleChange}
                      required
                    ></textarea>
                    <div className="invalid-feedback">
                      Forneça uma descrição.
                    </div>
                  </div>
                  <div className="col-12 d-flex justify-content-between">
                    <button className="btn btn-primary" type="submit">Atualizar</button>
                    <button className="btn btn-danger" type="button" onClick={handleDelete}>Excluir</button>
                  </div>
                </div>
              </div>
            </form>
            {/* Modal */}
            <Modal show={showModal} onHide={handleCloseModal} className='custom-modal' size="lg">
              <Modal.Header closeButton>
                <Modal.Title>{selectedExercise && selectedExercise.title}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>{formValues && formValues.description}</p>
                {formValues && formValues.url && formValues.url.toLowerCase().endsWith('.pdf') ? (
                  <AuthenticatedPDF
                    url={formValues.url}
                    title="PDF Viewer"
                    width="100%"
                    height="600px"
                  />
                ) : (
                  <Figure>
                    <AuthenticatedImage
                      as={Figure.Image}
                      width={800}
                      alt="171x180"
                      url={formValues.url}
                    />
                  </Figure>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Fechar
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        ) : (
          <p>Loading subjects...</p>
        )}
      </DashboardLayout>
    </div>
  );
};

export default ActivityEdit;
